import { Tile } from './constants';
import { HAZARD_CHARS, Hazard } from './hazards';
import type { Rng } from './rng';

/**
 * Arena definitions.
 *
 * Layouts are authored as ASCII so they are easy to eyeball and tweak:
 *   `#` indestructible block
 *   `.` open floor, eligible for a soft block during generation
 *   `_` open floor, never seeded with a soft block (lanes, decoration)
 *   `1`-`8` spawn points (also never seeded; their neighbours are cleared)
 *
 * and the hazards, which are open floor that never takes a soft block -- a
 * crate standing on a vent is a hazard the player cannot see (`HAZARD_CHARS`):
 *   `~` live wire      `*` eruption vent
 *   `^ v < >` fan, blowing the way the arrow points
 *   `=` ice            `,` undergrowth
 *
 * `tools/validate-maps.mjs` checks every layout is rectangular, has spawns and
 * is fully connected once soft blocks are bombed out.
 */
/** Which procedural surface recipe each part of an arena is built from. */
export interface ArenaSurfaces {
  hard: 'panel' | 'rock' | 'ice' | 'cobble' | 'circuit' | 'crate';
  soft: 'panel' | 'rock' | 'ice' | 'cobble' | 'circuit' | 'crate';
}

/**
 * What surrounds the arena.
 *
 * Each is a recipe for the sky gradient, the ring of distant shapes on the
 * horizon and the plinth the board sits on; see `src/render/backdrop.ts`.
 */
export type BackdropKind = 'city' | 'crags' | 'peaks' | 'canopy' | 'server' | 'stars';

/**
 * The shape of the world around the board, as opposed to its colour.
 *
 * This exists because of what a variant could not say. `ArenaVariant.theme` is
 * a `Partial<ArenaTheme>`, and until now `ArenaTheme` was twenty colours and
 * four enums -- so a variant could only ever be a palette swap, which is
 * exactly what the second looks turned out to be. The structural decisions all
 * lived in a table in `render/backdrop.ts`, keyed by `BackdropKind`, where
 * neither an arena nor a variant could reach them.
 *
 * Every field is optional and falls back to that table, so an arena that says
 * nothing renders exactly as it did. An arena or a variant that wants a
 * different *place* rather than a different paint can now say so.
 */
export interface ArenaScene {
  /** How many distant shapes to scatter around the ring. */
  count: number;
  /** Height range of a silhouette, before per-instance jitter. */
  height: [number, number];
  /** Footprint range. */
  width: [number, number];
  /** Emissive treatment baked into the silhouette shader. */
  pattern: 'windows' | 'fissure' | 'traces' | 'rim' | 'none';
  /** Silhouettes lean this many radians at most; 0 keeps them upright. */
  lean: number;
  /** Strength of the emissive pattern. */
  glow: number;
  /** Which theme colour the silhouettes are made of, and how far it is knocked back. */
  tint: { from: 'hard' | 'soft' | 'accent'; scale: number };
  /** Stars overhead, for the arenas that have a sky worth looking at. */
  stars: number;
  /** How far the plinth drops below the board. */
  plinth: number;
  /** How far pieces on the far side may rise past the board plane. */
  rise?: number;
  /** The slab the board stands on; see `plinthProfile` in backdrop.ts. */
  profile?: PlinthProfile;
  /** How the world darkens away from the board; see `addGround`. */
  depth?: SceneDepth;
  /** Readable objects in the middle distance; see `addProps`. */
  props?: PropKit;
  /** The overall colour grade; see `SceneGrade`. */
  grade?: SceneGrade;
  /**
   * A model to stand across the horizon instead of procedural silhouettes.
   *
   * The surround ring carries the depth of the room and, until there was a
   * bake-off, could not carry its subject: 145 lumps with a pattern painted on
   * them read as a skyline but never as racks. A model can, and instancing
   * makes it nearly free in memory -- one geometry and one texture set however
   * many stand there.
   *
   * It is deliberately a different file from the props beside the board.
   * Triangles here are multiplied by 145, so this wants the hardest decimation
   * that still reads at horizon distance, not the one chosen for a prop the
   * player can walk up to.
   */
  surround?: {
    model: string;
    file: string;
    /** How many to install. Rows are filled from the board outwards. */
    count?: number;
    /** Clear space between rows, as a multiple of the model's own depth. */
    rowGap?: number;
    /** Radius the first row stands at. Defaults to where silhouettes began. */
    from?: number;
    /** How full a row is packed, 0 to 1. Rows fill from the board outwards. */
    fill?: number;
  };
}

/**
 * The plinth, as a machined object rather than a line.
 *
 * The board used to end in a thin lit bar on a plain box. The bake-off
 * candidates all put the board on a thick slab with a raised rail around it, a
 * recessed fascia below, and lit corners -- which is most of why theirs reads
 * as built and ours read as drawn.
 */
export interface PlinthProfile {
  /** How far the slab oversails the grid on every side. */
  lip: number;
  /** Height of the rail standing above the board plane. */
  rail: number;
  /** How wide that rail is, measured inward from the slab edge. */
  railWidth: number;
  /** Segments the rail is broken into per side; 0 leaves it continuous. */
  railSegments: number;
  /** Height of the lit strip inset into the fascia, as a fraction of the drop. */
  trim: number;
  /** How far the trim is recessed from the slab face. */
  trimInset: number;
  /** Lit blocks at each corner of the rail; 0 for none. */
  corners: number;
}

/**
 * Readable objects between the plinth and the horizon.
 *
 * The silhouette ring is abstract shapes with an emissive pattern, and at the
 * distance the camera actually sees it that reads as texture rather than as
 * things. Every bake-off candidate has the opposite: a middle distance made of
 * objects you could name -- racks, pillars, seracs -- crowding the plinth.
 *
 * One parameterised kit rather than props authored per theme. Six themes times
 * five props is thirty models to make and keep looking right; a kit with a
 * shape and some numbers is one thing to maintain, and the difference between
 * a server rack and a temple pillar is mostly proportion and where the light
 * sits.
 */
export interface PropKit {
  /** Which assembly to build. */
  kind: 'rack';
  /** How many to place around the board. */
  count: number;
  /** Radius band, measured out from the edge of the plinth. */
  band: [number, number];
  /** Body footprint and height ranges. */
  size: { w: [number, number]; h: [number, number]; d: [number, number] };
  /** Rows of lit units down the face that points at the board. */
  rows: number;
  /** Brightness of those units. */
  glow: number;
  /** How far a prop behind the board may rise past the board plane. */
  rise: number;
  /**
   * Generated candidates to stand here instead of the boxes. Names of
   * directories under `assets/models/props`; instances are dealt round-robin
   * between them, so a frame holds the mix the arena would really have.
   */
  models?: string[];
  /**
   * Relative share of the ring each entry in `models` takes.
   *
   * An even deal is rarely the right composition. The reference this arena is
   * built from is a server room: racks fill it, a fan bank shows up every so
   * often, and the chips are on the board rather than standing around it. A
   * third of the surround being chips is not a smaller version of that, it is
   * a different room.
   *
   * Omitted means an even deal, which is what a ladder rung wants.
   */
  mix?: number[];
  /**
   * Which candidate each of those directories ships, after the bake-off.
   *
   * One entry per name in `models`, because the winners are not all from the
   * same service: judged side by side, the chip and the rack went to Tripo's
   * P2 route and the fan bank to Hi3D. A kit picked per prop rather than per
   * service is the whole reason the ring deals three models instead of one.
   *
   * `?props=` still overrides the lot with a single stem, which is what the
   * ladder needs -- a rung is only a comparison if every prop in the ring
   * changed together.
   */
  chosen?: Record<string, string>;
}

/**
 * The overall colour grade.
 *
 * The bake-off candidates are painted images, and a good part of what makes
 * them read as one place is that they were graded as one image: the deck sits
 * light and slightly desaturated against a room pushed dark and heavily toward
 * the accent, with the corners falling off. Ours came out of the renderer with
 * every part of the frame at roughly the same value, which is most of what is
 * left of the gap once the plinth and the props are built.
 *
 * Applied as a post pass, so it only runs where post already runs -- see
 * `applyGrade` in render/renderer.ts.
 */
export interface SceneGrade {
  /** Lifts the black point; small numbers, this is the whole frame. */
  lift: number;
  /** Multiplies everything after the lift. */
  gain: number;
  /** Above 1 darkens the mid-tones and is what deepens a room. */
  gamma: number;
  /** 1 leaves saturation alone, below 1 pulls colour out of the highlights. */
  saturation: number;
  /** Colour pushed into the shadows, usually the arena's own accent. */
  shadowTint: number;
  /** How much of that tint, 0 for none. */
  shadowTintAmount: number;
  /** Corner falloff, 0 for none. */
  vignette: number;
}

/** How the surround falls away in brightness, so the board reads as lit. */
export interface SceneDepth {
  /** Distance from the board where darkening begins. */
  near: number;
  /** Distance by which it has fallen all the way off. */
  far: number;
  /** How dark it gets, 0 for none and 1 for black. */
  strength: number;
  /** Extra darkening in the plinth's own shadow, close in. */
  contact: number;
}

export interface ArenaTheme {
  surfaces: ArenaSurfaces;
  /** Drifting atmosphere: embers, snow, spores and so on. */
  ambience: 'sparks' | 'embers' | 'snow' | 'spores' | 'motes' | 'dust';
  ambienceColor: number;
  /** The world beyond the board. */
  backdrop: BackdropKind;
  /**
   * Colour of the sky where it meets the ground, and of the fog. Distant
   * geometry fades into this rather than into the near-black zenith, which is
   * what stopped every arena reading as a board floating in an empty room.
   */
  horizon: number;
  floor: number;
  floorAlt: number;
  hard: number;
  hardTop: number;
  soft: number;
  softTop: number;
  sky: number;
  fogNear: number;
  fogFar: number;
  light: number;
  ambient: number;
  ground: number;
  accent: number;
  /**
   * Structure, not colour. Optional: omitted means the `backdrop` kind's own
   * recipe, which is how every arena rendered before this existed.
   */
  scene?: Partial<ArenaScene>;
}

/** An alternate look: a name for the picker, and what it changes. */
export interface ArenaVariant {
  name: string;
  theme: Partial<ArenaTheme>;
}

export interface ArenaDef {
  id: string;
  name: string;
  blurb: string;
  layout: string[];
  theme: ArenaTheme;
  /** What the base theme is called, for arenas that have more than one look. */
  lookName?: string;
  /**
   * Alternate looks for the same layout.
   *
   * A variant is presentation only -- it overrides parts of the theme and
   * nothing else -- so the grid, the spawns, the soft-block density and the
   * simulation are identical whichever one is showing. That is what lets the
   * index come from the match seed: the client and the Durable Object derive
   * the same number without a byte on the wire, everyone in a room sees the
   * same arena, and it changes between matches.
   *
   * Written as partial overrides rather than whole themes. A theme is twenty
   * fields and a variant usually differs in six; copying the rest is how the
   * two quietly drift apart.
   */
  variants?: ArenaVariant[];
  /** Fraction of eligible floor tiles seeded with soft blocks. */
  softDensity: number;
  /** Music track key resolved by the audio manager. */
  track: string;
}

export const ARENAS: ArenaDef[] = [
  {
    id: 'neon-grid',

    name: 'Neon Grid',
    blurb: 'The classic lattice, rendered in synthwave chrome.',
    softDensity: 0.8,
    track: 'battle-neon',
    layout: [
      '###############',
      '#1...........2#',
      '#.#~#.#~#.#~#.#',
      '#.............#',
      '#.#.#~#.#~#.#.#',
      '#.............#',
      '#~#.#~#.#~#.#~#',
      '#.............#',
      '#.#.#~#.#~#.#.#',
      '#.............#',
      '#.#~#.#~#.#~#.#',
      '#3...........4#',
      '###############',
    ],
    theme: {
      surfaces: { hard: 'panel', soft: 'crate' },
      ambience: 'sparks',
      ambienceColor: 0x35f5ff,
      backdrop: 'city',
      horizon: 0x364d8b,
      floor: 0x3d467f,
      floorAlt: 0x4e5ba1,
      hard: 0x30333d,
      hardTop: 0x5a5f6b,
      soft: 0x7b3ac4,
      softTop: 0xd77bff,
      sky: 0x1c203e,
      fogNear: 18,
      fogFar: 44,
      light: 0xc9d4ff,
      ambient: 0x7565d9,
      ground: 0x22264c,
      accent: 0x35f5ff,
    },
  },
  {
    id: 'magma-core',
    name: 'Magma Core',
    blurb: 'Open lanes and long sightlines. Nowhere to hide.',
    softDensity: 0.72,
    track: 'battle-magma',
    layout: [
      '###############',
      '#1..._*_*_...2#',
      '#.##.#___#.##.#',
      '#....._*_.....#',
      '#_##.......##_#',
      '#_.._##_##.._.#',
      '#__.*#_*_#*.__#',
      '#_.._##_##.._.#',
      '#_##.......##_#',
      '#....._*_.....#',
      '#.##.#___#.##.#',
      '#3..._*_*_...4#',
      '###############',
    ],
    theme: {
      surfaces: { hard: 'rock', soft: 'crate' },
      ambience: 'embers',
      ambienceColor: 0xff8a3c,
      backdrop: 'crags',
      horizon: 0x852b1a,
      floor: 0x4b3834,
      floorAlt: 0x5f4940,
      hard: 0x453127,
      hardTop: 0x5a3d31,
      soft: 0x8f4a1c,
      softTop: 0xf59f3d,
      sky: 0x361612,
      fogNear: 16,
      fogFar: 40,
      light: 0xffd0a0,
      ambient: 0xa54724,
      ground: 0x2c1814,
      accent: 0xff6a1f,
    },
    lookName: 'Molten',
    variants: [
      // Cooled: the flow has crusted over. Same rock, less of it lit, and a
      // grey-violet sky instead of a red one.
      {
        name: 'Cooled',
        theme: {
          floor: 0x3a3230, floorAlt: 0x4a403c, hard: 0x2e2724, hardTop: 0x463b34,
          soft: 0x6e4a20, softTop: 0xd8913f, sky: 0x1c1a20, horizon: 0x4a3a44,
          accent: 0xff9a4a, ambienceColor: 0xff9a4a, ground: 0x2a2422, light: 0xffd6b0,
        },
      },
    ],
  },
  {
    id: 'glacier',

    name: 'Glacier Vault',
    blurb: 'Tight corridors and dead ends. Kick bombs, not tempers.',
    softDensity: 0.7,
    track: 'battle-glacier',
    layout: [
      '###############',
      '#1.._#=_=#_..2#',
      '#.##..=_=..##.#',
      '#..#.......#..#',
      '#=.#.##=##.#.=#',
      '#...=.===.=...#',
      '#.#=#==#==#=#.#',
      '#...=.===.=...#',
      '#=.#.##=##.#.=#',
      '#..#.......#..#',
      '#.##..=_=..##.#',
      '#3.._#=_=#_..4#',
      '###############',
    ],
    theme: {
      surfaces: { hard: 'rock', soft: 'ice' },
      ambience: 'snow',
      ambienceColor: 0xdff4ff,
      backdrop: 'peaks',
      horizon: 0x296988,
      floor: 0x315467,
      floorAlt: 0x407a9b,
      hard: 0x22323f,
      hardTop: 0x3e5967,
      soft: 0x62c6d8,
      softTop: 0xd8f7ff,
      sky: 0x16364a,
      fogNear: 17,
      fogFar: 41,
      light: 0xdcf3ff,
      ambient: 0x478cb5,
      ground: 0x163448,
      accent: 0x7ef4ff,
    },
  },
  {
    id: 'overgrowth',

    name: 'Overgrowth',
    blurb: 'A ruined courtyard reclaimed by the jungle. Four-way symmetry.',
    softDensity: 0.75,
    track: 'battle-jungle',
    layout: [
      '###############',
      '#1...........2#',
      '#_###._._.###_#',
      '#.#....,....#.#',
      '#,.###,,,###.,#',
      '#....#,.,#....#',
      '#.#.,.,#,.,.#.#',
      '#....#,.,#....#',
      '#,.###,,,###.,#',
      '#.#....,....#.#',
      '#_###._._.###_#',
      '#3...........4#',
      '###############',
    ],
    theme: {
      surfaces: { hard: 'cobble', soft: 'crate' },
      ambience: 'spores',
      ambienceColor: 0xbdff7a,
      backdrop: 'canopy',
      horizon: 0x395b31,
      floor: 0x445742,
      floorAlt: 0x596f52,
      hard: 0x44513f,
      hardTop: 0x76856a,
      soft: 0x7a4415,
      softTop: 0xdd8f38,
      sky: 0x162e16,
      fogNear: 16,
      fogFar: 38,
      light: 0xe8ffd0,
      ambient: 0x4e8c47,
      ground: 0x1e2c20,
      accent: 0x8fd94f,
    },
  },
  {
    id: 'circuit',
    name: 'Circuit Board',
    blurb: 'Wide open with pillar clusters. Built for eight-player chaos.',
    softDensity: 0.66,
    track: 'battle-circuit',
    layout: [
      '#################',
      '#1...._..._....2#',
      '#.##._.###._.##.#',
      '#~~~~.._7_..~~~~#',
      '#~##..#._.#..##~#',
      '#...._.._.._....#',
      '#5#._##___##_.#6#',
      '#...._.._.._....#',
      '#~##..#._.#..##~#',
      '#~~~~.._8_..~~~~#',
      '#.##._.###._.##.#',
      '#3...._..._....4#',
      '#################',
    ],
    theme: {
      surfaces: { hard: 'circuit', soft: 'crate' },
      ambience: 'motes',
      ambienceColor: 0x3fe08a,
      backdrop: 'server',
      // Pulled well down from the deck. The candidate's room is nearly black
      // and its board is not, and that separation is most of what says the
      // board is lit and the room is not -- a grade can deepen it but cannot
      // create it, and only high and ultra run a grade at all.
      horizon: 0x15361f,
      // Greyer and a step lighter, which is the candidate's deck: the old pair
      // sat at the same value as the blocks standing on them.
      floor: 0x33443c,
      floorAlt: 0x3e5349,
      hard: 0x1e3e31,
      hardTop: 0x3e7959,
      soft: 0x8a5a14,
      softTop: 0xf0a63a,
      sky: 0x0b1711,
      fogNear: 19,
      fogFar: 48,
      light: 0xd8ffe8,
      ambient: 0x2a6349,
      ground: 0x0d1a14,
      accent: 0x3fe08a,
      // The first arena built as a place rather than a palette. Read off the
      // bake-off candidate: the board stands on a machined slab with a
      // segmented rail, a lit strip recessed into the fascia and lit corners,
      // and the room around it falls away into the dark instead of being lit
      // as evenly at forty units as at four.
      scene: {
        profile: {
          lip: 1.3,
          rail: 0.52,
          railWidth: 0.66,
          // Nine panels a side. The count matters more than the height: an
          // unbroken extrusion reads as a border drawn around the board.
          railSegments: 9,
          trim: 0.17,
          trimInset: 0.07,
          corners: 0.72,
        },
        depth: { near: 10, far: 46, strength: 0.62, contact: 0.28 },
        /*
         * A wall of racks behind the near ring, at 1,266 triangles each.
         *
         * Which candidate goes here is decided by decimation, not by looks.
         * `tripo-smart` has a hard floor at 4,208 triangles however low the
         * target goes -- 54 disconnected parts, and a simplifier cannot
         * collapse across a part boundary -- while `hi3d-image` reaches 1,266
         * because it is four. That is 3.3x, and it is multiplied by three
         * hundred, so it decides whether this ring is a wall or a scatter.
         *
         * Textures stay at 1024: the first attempt here used 512 and read
         * blurry against a near ring at 1024, which is a setting rather than a
         * model and was the wrong place to save.
         */
        surround: { model: 'rack', file: 'hi3d-image.prep1k.etc1s', count: 300, from: 16 },
        // The room the board is standing in. The candidate's middle distance
        // is racks close enough to read the units on, so these sit just past
        // the plinth rather than out where the silhouette ring lives.
        props: {
          kind: 'rack',
          count: 46,
          // Narrow, so the detailed racks form one tight rank against the
          // plinth instead of a thin scatter across eight units. The dense
          // rows behind them start where this band ends.
          band: [1.6, 3.6],
          // Wider than tall, and not by much: the first pass ranged to 5.2 and
          // built a skyline, which is the backdrop this arena already had.
          // A rack is a squat box with a lot of small lights on it.
          size: { w: [1.9, 3.1], h: [1.2, 3.4], d: [1.4, 2.2] },
          // Nine rows, not five. The count is what makes a face read as rack
          // units rather than as windows, and windows is what five looked like.
          rows: 9,
          glow: 1.1,
          rise: 3.4,
          // All three props, dealt round the ring, so a frame holds the mix
          // the arena would actually have rather than one model forty-six
          // times. See `?props=` in backdrop.ts.
          // Read off the reference: racks fill the room, fan banks punctuate
          // it. The chip is not here because in the reference it is a board
          // block, and the board draws its own.
          models: ['rack', 'fans'],
          mix: [6, 1],
          /*
           * The bake-off result, judged in the viewer at prep20.
           *
           * Ranked on looks, then priced, and the price disagreed with the
           * intuition. This kit carries twice the geometry of the one ranked
           * on size -- 63,179 unique triangles against 31,092 -- and uses ten
           * megabytes *less* memory, because geometry is 2% of what a prop
           * costs and texture maps are 92%. `tripo-smart` ships one map where
           * Tripo's P2 rack ships three, and one map is 5.3MB whether it wraps
           * nine thousand triangles or nine hundred thousand. Instancing is
           * what separates the two budgets: forty-six copies cost forty-six
           * times the vertex work and one time the memory.
           *
           * The chip is the second-ranked of a pair called indistinguishable.
           * Its twin was generated with Meshy's `image_enhancement` on, which
           * splits 9.4% of its vertices against 3.2% -- UV seams the decimator
           * cannot collapse across -- so it floors at 34,228 triangles where
           * this one reaches the 20,000 target. 215,685 triangles across the
           * ring, for a difference nobody could see.
           *
           * The rack was judged best from `047519f1`, the flat 2D reference
           * replaced mid-bake-off precisely because it was making models flat.
           * Four candidates built from the reference that replaced it lost to
           * it, which is a result and not an oversight.
           */
          /*
           * `.etc1s` on every one of them, which is a memory decision and not
           * a looks one.
           *
           * A 1024 square map is 5.3MB resident whatever container it arrived
           * in, because JPEG and WebP both decode to RGBA8. The eight maps
           * this room loads are 42.7MB of texture against about 2MB of
           * geometry. ETC1S is sampled by the GPU while still compressed --
           * half a byte per texel on the PowerVR this was measured on -- so
           * the same eight maps are 5.3MB, and 37MB comes back.
           *
           * The bill is on the wire and it is small: 2.1MB of downloads
           * becomes 2.6MB, once, into the cache. The other bill is quality,
           * measured at 33.4 dB PSNR on a base colour map, which is visible
           * at 3x magnification on a prop that renders about 200 pixels tall.
           * Drop the suffix on any of these to go back.
           */
          chosen: {
            rack: 'tripo-smart.prep20.etc1s',
            fans: 'hi3d-image.prep20.etc1s',
            // Kept though nothing places it: the bake-off chose it, and the
            // day the board's blocks stop being procedural this is the model
            // they become. Dropping the line would lose that decision.
            chip: 'Meshy_smart_noenhancement_manual.prep20.etc1s',
          },
        },
        // Graded as one image rather than lit as six objects: the mid-tones
        // deepened so the room falls into the dark, a little colour pulled out
        // so the deck reads grey-green rather than green, the accent pushed
        // into the shadows only, and the corners falling off.
        grade: {
          lift: 0.008,
          gain: 1.06,
          gamma: 1.14,
          saturation: 0.88,
          shadowTint: 0x3fe08a,
          shadowTintAmount: 0.3,
          vignette: 0.34,
        },
      },
    },
    lookName: 'Green mask',
    variants: [
      /**
       * Blue solder mask -- and a different board, not the same one repainted.
       *
       * This is what `scene` was added for. A variant is a
       * `Partial<ArenaTheme>`, so until the theme could describe structure the
       * only thing a second look could be was a colour swap, which is exactly
       * what this one was. It now sits on a taller slab with a heavier rail cut
       * into fewer, longer panels, packs the room in tighter and lets it fall
       * off harder -- a rack aisle rather than an open floor.
       */
      {
        name: 'Blue mask',
        theme: {
          floor: 0x1d3040, floorAlt: 0x27435a, hard: 0x17384f, hardTop: 0x2f6d94,
          sky: 0x0f1b26, horizon: 0x1c4460, accent: 0x35d3f5, ambienceColor: 0x35d3f5,
          ground: 0x14232f,
          scene: {
            plinth: 4.2,
            count: 190,
            height: [2.6, 12],
            width: [1.4, 2.8],
            profile: {
              lip: 1.45,
              rail: 0.74,
              railWidth: 0.8,
              railSegments: 5,
              trim: 0.13,
              trimInset: 0.09,
              corners: 0.86,
            },
            depth: { near: 8, far: 40, strength: 0.74, contact: 0.36 },
          },
        },
      },
    ],
  },
  {
    id: 'the-void',
    name: 'The Void',
    blurb: 'No walls, no mercy. A floating slab above nothing at all.',
    softDensity: 0.82,
    track: 'battle-void',
    layout: [
      '###############',
      '#1.....7.....2#',
      '#.............#',
      '#..#>>>>>>>#..#',
      '#.............#',
      '#....#.v.#....#',
      '#5.#._._._.#.6#',
      '#....#.^.#....#',
      '#.............#',
      '#..#<<<<<<<#..#',
      '#.............#',
      '#3.....8.....4#',
      '###############',
    ],
    theme: {
      surfaces: { hard: 'panel', soft: 'ice' },
      ambience: 'dust',
      ambienceColor: 0xc9a8ff,
      backdrop: 'stars',
      horizon: 0x342353,
      floor: 0x383846,
      floorAlt: 0x4e4e64,
      hard: 0x24242f,
      hardTop: 0x3f3f55,
      soft: 0x7a4ad0,
      softTop: 0xc9a8ff,
      sky: 0x10101a,
      fogNear: 14,
      fogFar: 36,
      light: 0xe0e0ff,
      ambient: 0x47476e,
      ground: 0x080808,
      accent: 0xff4fd8,
    },
    lookName: 'Violet drift',
    variants: [
      // A colder nebula. The slab is the same; the sky it hangs in is not.
      {
        name: 'Teal nebula',
        theme: {
          floor: 0x2f3a44, floorAlt: 0x3c4a56, hard: 0x1e2830, hardTop: 0x36444f,
          soft: 0x2f7f86, softTop: 0x7fe0e8, sky: 0x0a1218, horizon: 0x1a4450,
          accent: 0x4fe8d8, ambienceColor: 0x4fe8d8, ground: 0x0d1a20,
        },
      },
    ],
  },
];

/**
 * Training ground. Deliberately empty: the tutorial places exactly the blocks,
 * pickups and opponents each drill needs, so nothing else is in the way.
 */
export const DOJO: ArenaDef = {
  id: 'dojo',
  name: 'Training Ground',
  blurb: 'A quiet arena with nothing in it but the drill you are doing.',
  softDensity: 0,
  track: 'menu',
  layout: [
    '###############',
    '#1..........._#',
    '#_###_###_###_#',
    '#_..........._#',
    '#_###_###_###_#',
    '#_..........._#',
    '#_..........._#',
    '#_..........._#',
    '#_###_###_###_#',
    '#_..........._#',
    '#_###_###_###_#',
    '#_...........2#',
    '###############',
  ],
  theme: {
    surfaces: { hard: 'panel', soft: 'crate' },
    ambience: 'motes',
    ambienceColor: 0x35f5ff,
      backdrop: 'city',
      horizon: 0x364d8b,
    floor: 0x424b7a,
    floorAlt: 0x4e598b,
    hard: 0x5866ab,
    hardTop: 0x808fe7,
    soft: 0x8f4bd6,
    softTop: 0xd88bff,
    sky: 0x181c2e,
    fogNear: 20,
    fogFar: 48,
    light: 0xd8e2ff,
    ambient: 0x5e65a5,
    ground: 0x202644,
    accent: 0x35f5ff,
  },
};

export function getArena(id: string): ArenaDef {
  if (id === DOJO.id) return DOJO;
  return ARENAS.find((a) => a.id === id) ?? ARENAS[0];
}

/**
 * The names of every look this arena can wear, base first.
 *
 * Empty for an arena with a single look, which is what the picker checks
 * before it offers a choice at all.
 */
export function arenaLooks(arena: ArenaDef): string[] {
  if (!arena.variants || arena.variants.length === 0) return [];
  return [arena.lookName ?? arena.name, ...arena.variants.map((v) => v.name)];
}

/**
 * Which look a seed resolves to. The base theme is look 0, so an arena with
 * two entries in `variants` has three.
 *
 * The index comes from the seed rather than from a random roll, because the
 * roll would have to be broadcast to keep a room in agreement, and a value
 * both sides can compute is cheaper than one either side has to send.
 *
 * The mix is deliberately not `seed % n`: seeds are handed out in ways that can
 * correlate with low bits, and an arena that only ever shows two of its three
 * looks is a bug nobody reports.
 */
export function arenaLookIndex(arena: ArenaDef, seed: number): number {
  const count = (arena.variants?.length ?? 0) + 1;
  if (count === 1) return 0;
  let h = (seed ^ 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
  // `>>> 0` is load-bearing: XOR yields a *signed* int32 in JS, so without it
  // this goes negative whenever the top bit is set, and a negative remainder
  // indexes past the start of the array and silently falls back to the base
  // theme. Measured over 60k seeds that turned a 50/50 split into 50/25.
  return ((h ^ (h >>> 16)) >>> 0) % count;
}

/** The arena wearing a particular look. Out-of-range indices give the base. */
export function arenaWithLook(arena: ArenaDef, look: number): ArenaDef {
  const variant = arena.variants?.[look - 1];
  if (!variant) return arena;
  const theme: ArenaTheme = { ...arena.theme, ...variant.theme };
  // `scene` is a partial like the theme holding it, so it has to merge the
  // same way. A plain spread replaces it whole, and then a variant describing
  // only its own plinth silently loses everything the base described: Blue
  // mask sets a slab and a depth falloff, and with it went all 46 of
  // Circuit's racks and the colour grade -- 5,520 triangles and a graded
  // image, gone on half the seeds, with nothing in the picker to say so.
  //
  // One level deep is the right depth, not a general deep merge. `profile`,
  // `depth`, `props` and `grade` are whole types rather than partials, so a
  // variant that supplies one supplies all of it.
  if (arena.theme.scene || variant.theme.scene) {
    theme.scene = { ...arena.theme.scene, ...variant.theme.scene };
  }
  return { ...arena, theme };
}

/** The arena as it should look for a given match. */
export function arenaVariant(arena: ArenaDef, seed: number): ArenaDef {
  return arenaWithLook(arena, arenaLookIndex(arena, seed));
}

/**
 * A seed that makes `arena` wear the look at `look`.
 *
 * This is how a chosen look reaches the board without becoming something the
 * match has to carry: the look stays derived from the seed everywhere, and the
 * choice only decides which seed gets rolled. So a lobby needs no new field in
 * a snapshot, and a client that has never heard of looks still agrees.
 *
 * The scan is a forward walk rather than a retry loop so it always terminates.
 * With at most three looks the first candidate usually lands.
 */
export function seedForLook(arena: ArenaDef, look: number): number {
  const start = (Math.random() * 0x7fffffff) | 0;
  const count = (arena.variants?.length ?? 0) + 1;
  if (look < 0 || look >= count) return start;
  for (let i = 0; i < 4096; i++) {
    const seed = (start + i) % 0x7fffffff;
    if (arenaLookIndex(arena, seed) === look) return seed;
  }
  return start;
}

/** How many players an arena can seat, derived from its spawn markers. */
export function arenaCapacity(arena: ArenaDef): number {
  let n = 0;
  for (const row of arena.layout) {
    for (const ch of row) if (ch >= '1' && ch <= '8') n++;
  }
  return n;
}

export interface BuiltMap {
  width: number;
  height: number;
  grid: Uint8Array;
  /** Flat hazard overlay, parallel to `grid`. See `shared/hazards.ts`. */
  hazards: Uint8Array;
  /** Spawn cells in slot order. */
  spawns: Array<[number, number]>;
}

/**
 * Turns an arena's ASCII layout into a tile grid, seeding soft blocks with the
 * supplied RNG so client and server agree on the starting board.
 */
export function buildMap(arena: ArenaDef, rng: Rng, density = arena.softDensity): BuiltMap {
  const rows = arena.layout;
  const height = rows.length;
  const width = Math.max(...rows.map((r) => r.length));
  const grid = new Uint8Array(width * height);
  const hazards = new Uint8Array(width * height);
  const spawns: Array<[number, number]> = [];
  const eligible: number[] = [];
  const protectedCells = new Set<number>();

  for (let y = 0; y < height; y++) {
    const row = rows[y].padEnd(width, '#');
    for (let x = 0; x < width; x++) {
      const ch = row[x];
      const idx = y * width + x;
      if (ch === '#') {
        grid[idx] = Tile.HARD;
      } else {
        grid[idx] = Tile.EMPTY;
        if (ch >= '1' && ch <= '8') spawns[Number(ch) - 1] = [x, y];
        else if (ch === '.') eligible.push(idx);
        else hazards[idx] = HAZARD_CHARS[ch] ?? Hazard.NONE;
      }
    }
  }

  // Keep a pocket around every spawn clear so nobody starts boxed in.
  for (const spawn of spawns) {
    if (!spawn) continue;
    const [sx, sy] = spawn;
    for (const [dx, dy] of SPAWN_CLEARANCE) {
      const nx = sx + dx;
      const ny = sy + dy;
      if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
      const idx = ny * width + nx;
      if (grid[idx] !== Tile.HARD) protectedCells.add(idx);
    }
  }

  const candidates = rng.shuffle(eligible.filter((i) => !protectedCells.has(i)));
  const count = Math.floor(candidates.length * density);
  for (let i = 0; i < count; i++) grid[candidates[i]] = Tile.SOFT;

  return { width, height, grid, hazards, spawns: spawns.filter(Boolean) };
}

const SPAWN_CLEARANCE: ReadonlyArray<readonly [number, number]> = [
  [0, 0],
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [2, 0],
  [-2, 0],
  [0, 2],
  [0, -2],
  [1, 1],
  [-1, -1],
  [1, -1],
  [-1, 1],
];

/** Sudden-death block order: an inward clockwise spiral over interior tiles. */
export function spiralOrder(width: number, height: number): number[] {
  const out: number[] = [];
  let top = 1;
  let bottom = height - 2;
  let left = 1;
  let right = width - 2;
  while (top <= bottom && left <= right) {
    for (let x = left; x <= right; x++) out.push(top * width + x);
    top++;
    for (let y = top; y <= bottom; y++) out.push(y * width + right);
    right--;
    if (top <= bottom) {
      for (let x = right; x >= left; x--) out.push(bottom * width + x);
      bottom--;
    }
    if (left <= right) {
      for (let y = bottom; y >= top; y--) out.push(y * width + left);
      left++;
    }
  }
  return out;
}
